import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';

const API = import.meta.env.VITE_API_URL || '';

const ConfirmAccount = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading'); // 'loading', 'success', 'error'
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState('');

  useEffect(() => {
    const token = searchParams.get('token');
    
    if (!token) {
      setStatus('error');
      setMessage('Invalid confirmation link. Please check your email and try again.');
      return;
    }

    const confirmAccount = async () => {
      try {
        const response = await axios.get(`${API}/api/auth/confirm-account?token=${token}`);
        
        if (response.data.success) {
          setStatus('success');
          setEmail(response.data.email || '');
          setMessage('Your account has been confirmed as your primary account.');
        } else {
          setStatus('error');
          setMessage(response.data.message || 'Failed to confirm account.');
        }
      } catch (error) {
        setStatus('error');
        const errorMsg = error.response?.data?.detail || 'Failed to confirm account. The link may have expired.';
        setMessage(errorMsg);
      }
    };

    confirmAccount();
  }, [searchParams]);

  return (
    <div 
      className="min-h-screen flex items-center justify-center p-4"
      style={{
        background: 'linear-gradient(135deg, #1a0527 0%, #2D063E 50%, #1a0527 100%)'
      }}
    >
      <div 
        className="max-w-md w-full rounded-2xl p-8 text-center"
        style={{
          background: 'rgba(45, 6, 62, 0.8)',
          border: '1px solid rgba(223, 18, 131, 0.3)',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)'
        }}
      >
        {status === 'loading' && (
          <>
            <Loader2 className="w-16 h-16 mx-auto mb-6 text-[#df1283] animate-spin" />
            <h1 className="text-2xl font-bold text-white mb-3">Confirming Your Account</h1>
            <p className="text-white/60">Please wait...</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-green-500/20 flex items-center justify-center">
              <CheckCircle className="w-12 h-12 text-green-400" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-3">Account Confirmed!</h1>
            {email && (
              <p className="text-[#0FFFE2] font-medium mb-4">{email}</p>
            )}
            <p className="text-white/70 mb-8">{message}</p>
            <p className="text-white/50 text-sm mb-6">
              Any duplicate accounts under your name will be reviewed by our team. Your journal entries are safe.
            </p>
            <Button
              onClick={() => navigate('/login')}
              className="w-full bg-gradient-to-r from-[#df1283] to-[#FF7901] hover:opacity-90"
            >
              Go to Login
            </Button>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-red-500/20 flex items-center justify-center">
              <XCircle className="w-12 h-12 text-red-400" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-3">Confirmation Failed</h1>
            <p className="text-white/70 mb-8">{message}</p>
            <div className="space-y-3">
              <Button
                onClick={() => navigate('/login')}
                className="w-full bg-gradient-to-r from-[#df1283] to-[#FF7901] hover:opacity-90"
              >
                Go to Login
              </Button>
              <Button
                onClick={() => navigate('/')}
                variant="outline"
                className="w-full border-[#4A0A66] text-white hover:bg-[#4A0A66]/30"
              >
                Back to Home
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ConfirmAccount;
